import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse, CancelToken } from 'axios';
import qs from 'qs';

type UnknownObject = Record<string, unknown>;

export const generateQueryString = (params: UnknownObject, useExplicitIndices = false): string => {
  const arrayFormat = useExplicitIndices ? 'indices' : 'brackets';

  return qs.stringify(params, { arrayFormat });
};

const getCSRFHeader = (): AxiosRequestHeaders => {
  const csrfToken = document?.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  const headers = new axios.AxiosHeaders();
  if (csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }
  return headers;
};

const getRequestHeaders = () => {
  return {
    ...getCSRFHeader(),
  };
};

const isHousecallRequest = (urlPath: string) => {
  let url;

  try {
    url = new URL(urlPath);
  } catch (error) {
    return true;
  }
  const isAbsoluteUrl = url.protocol === 'http:' || url.protocol === 'https:';
  return !isAbsoluteUrl;
};

const getDeviceContext = () => {
  const deviceContext = JSON.stringify({
    os: {
      name: 'Web',
    },
    userAgent: navigator.userAgent,
  });

  return btoa(deviceContext);
};

const paymentRequiredHandlers: Array<(response: AxiosResponse) => void> = [];

const handlePaymentRequired = (response: AxiosResponse) => {
  paymentRequiredHandlers.forEach((handler) => {
    handler(response);
  });
};

declare module 'amplitude-js' {
  export interface AmplitudeClient {
    _sessionId?: string;
  }
}

const newHttpClient = (baseUrl?: string) => {
  const axiosConfig = window.BOOK_HOUSECALL_URL
    ? {
        baseURL: baseUrl || window.BOOK_HOUSECALL_URL,
        withCredentials: true,
      }
    : undefined;

  const httpClient = axios.create(axiosConfig);

  httpClient.interceptors.request.use((config) => {
    if (isHousecallRequest(config.url!)) {
      // eslint-disable-next-line no-underscore-dangle
      const sessionId = window.amplitude?.getInstance()?._sessionId;

      if (sessionId) {
        httpClient.defaults.headers.common['Amplitude-Session-Id'] = sessionId;
      }
      httpClient.defaults.headers.common['Segment-Device-Context'] = getDeviceContext();
    }
    return config;
  });

  httpClient.interceptors.response.use(
    (response) => {
      if (response.status === 402) {
        handlePaymentRequired(response);
      }

      return response;
    },
    (error) => {
      if (error.response && error.response.status === 402) {
        handlePaymentRequired(error.response);
      }

      return Promise.reject(error);
    }
  );

  return httpClient;
};

export const httpClientDefault = newHttpClient();
export const hcpServerHttpClient = newHttpClient(window.HOUSECALL_SERVER_URL);

const sendRequestDefault = <ResponseObject = never, PayloadObject = never>(config: AxiosRequestConfig<PayloadObject>) =>
  httpClientDefault.request<ResponseObject>(config);

const sendRequestHcpServer = <ResponseObject = never, PayloadObject = never>(
  config: AxiosRequestConfig<PayloadObject>
) => hcpServerHttpClient.request<ResponseObject>(config);

const httpMethods = {
  GET: 'GET',
  POST: 'POST',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
  PUT: 'PUT',
} as const;

const RestAPIGenerator = (sendRequest: typeof sendRequestDefault) => ({
  getWithFormData<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    formData?: PayloadObject,
    cancelToken?: CancelToken,
    signal?: AbortSignal
  ) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.GET,
      params: formData,
      cancelToken,
      signal,
    });
  },

  getWithQueryString<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    formData?: PayloadObject,
    headers: Record<string, string> = {}
  ) {
    const normalizedHeaders = new axios.AxiosHeaders(headers);

    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: { ...getRequestHeaders(), ...normalizedHeaders },
      method: httpMethods.GET,
      params: formData,
      paramsSerializer(params) {
        return generateQueryString(params);
      },
    });
  },

  get<ResponseObject = never, PayloadObject = never>(requestUrl: string, options: UnknownObject = {}) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.GET,
      ...options,
    });
  },

  getWithHeaders<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    headers: Record<string, string> = {}
  ) {
    const normalizedHeaders = new axios.AxiosHeaders(headers);
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: {
        ...getRequestHeaders(),
        ...normalizedHeaders,
      },
      method: httpMethods.GET,
    });
  },

  postWithFormData<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    formData?: PayloadObject,
    cancelToken?: CancelToken,
    signal?: AbortSignal
  ) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.POST,
      data: formData,
      cancelToken,
      signal,
    });
  },

  postWithCustomHeaders<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    requestHeaders: AxiosRequestHeaders,
    formData?: PayloadObject,
    restConfigs: AxiosRequestConfig = {}
  ) {
    const normalHeaders = getRequestHeaders();
    const headers = {
      ...normalHeaders,
      ...requestHeaders,
    };

    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers,
      method: httpMethods.POST,
      ...(formData && { data: formData }),
      ...restConfigs,
    });
  },
});

const RestAPI = RestAPIGenerator(sendRequestDefault);
export const PaymentRestAPI = RestAPIGenerator(sendRequestHcpServer);

export default RestAPI;
